import {
  LicenseCode,
  LicensePaymentStatus,
  type LicenseTypeListItem,
  OwnershipType,
  type PartyLicenseDto,
  Region,
} from "Infrastructure/Api/Api";

import cz_bronze from "Assets/Images/Licenses/LOHCZ-Bronze.png";
import cz_gold from "Assets/Images/Licenses/LOHCZ-Gold.png";
import cz_silver from "Assets/Images/Licenses/LOHCZ-Silver.png";
import cz_standard from "Assets/Images/Licenses/LOHCZ-Standard.png";
import sk_bronze from "Assets/Images/Licenses/LOHSK-Bronze.png";
import sk_gold from "Assets/Images/Licenses/LOHSK-Gold.png";
import sk_silver from "Assets/Images/Licenses/LOHSK-Silver.png";
import sk_standard from "Assets/Images/Licenses/LOHSK-Standard.png";
import type { LicenseSelectionModel } from "Components/Licenses/LicenseOrderForm/schema";

export const LICENSE_CODE_FILTER_OPTIONS = Object.values(LicenseCode).map(
  (code) => ({
    label: code,
    value: code,
  }),
);

export const LICENSE_CODE_TO_DATA_MAP: Record<
  LicenseCode,
  { title: string; czImg: string; skImg: string }
> = {
  [LicenseCode.Gold]: {
    title: "Gold",
    czImg: cz_gold,
    skImg: sk_gold,
  },
  [LicenseCode.Silver]: {
    title: "Silver",
    czImg: cz_silver,
    skImg: sk_silver,
  },
  [LicenseCode.Bronze]: {
    title: "Bronze",
    czImg: cz_bronze,
    skImg: sk_bronze,
  },
  [LicenseCode.Standard]: {
    title: "Standard",
    czImg: cz_standard,
    skImg: sk_standard,
  },
};

export const getLicenseImage = (code: LicenseCode, region: Region): string => {
  const { czImg, skImg } = LICENSE_CODE_TO_DATA_MAP[code];
  return region === Region.CZ ? czImg : skImg;
};

export const LICENSE_CODE_TO_PERKS_MAP: Record<
  LicenseCode,
  { terminals: number; subAccounts: number }
> = {
  [LicenseCode.Gold]: { terminals: 300, subAccounts: 10 },
  [LicenseCode.Silver]: { terminals: 100, subAccounts: 5 },
  [LicenseCode.Bronze]: { terminals: 50, subAccounts: 2 },
  [LicenseCode.Standard]: { terminals: 1, subAccounts: 0 },
};

export const transformLicenseToDefaultValues = (
  license: PartyLicenseDto,
): LicenseSelectionModel => ({
  code: license.code,
  walletAddress: license.walletAddress ?? undefined,
  isUsingPrivateWallet: license.ownWallet === OwnershipType.NonCustodial,
  region: license.region,
});

export type LicenseResumeState =
  | { kind: "unpaid"; unpaidLicenses: Array<PartyLicenseDto> }
  | { kind: "paid"; license: PartyLicenseDto }
  | { kind: "none" };

const isPaidOrProcessing = ({ paymentStatus }: PartyLicenseDto) =>
  paymentStatus === LicensePaymentStatus.Processing ||
  paymentStatus === LicensePaymentStatus.Success;

// An order on the server is the only durable proof that an onboarding hunter reached the
// payment step — the wizard's own step lives in client state and dies with a logout/reload.
export const getLicenseResumeState = (
  licenses: Array<PartyLicenseDto>,
): LicenseResumeState => {
  const unpaidLicenses = licenses.filter(
    ({ paymentStatus }) => paymentStatus === LicensePaymentStatus.Initiated,
  );

  if (unpaidLicenses.length > 0) {
    return { kind: "unpaid", unpaidLicenses };
  }

  const paidLicense = licenses.find(isPaidOrProcessing);

  if (paidLicense) {
    return { kind: "paid", license: paidLicense };
  }

  return { kind: "none" };
};

const normalizeWalletAddress = (walletAddress?: string) =>
  (walletAddress ?? "").trim();

export const isSameAsOrderedLicense = (
  {
    code,
    isUsingPrivateWallet,
    walletAddress,
    couponCode,
  }: Pick<
    LicenseSelectionModel,
    "code" | "isUsingPrivateWallet" | "walletAddress" | "couponCode"
  >,
  orderedLicense: PartyLicenseDto,
): boolean => {
  const ordered = transformLicenseToDefaultValues(orderedLicense);
  const isSameWallet =
    Boolean(isUsingPrivateWallet) === ordered.isUsingPrivateWallet &&
    (!isUsingPrivateWallet ||
      normalizeWalletAddress(walletAddress) ===
        normalizeWalletAddress(ordered.walletAddress));

  return !couponCode && code === ordered.code && isSameWallet;
};

export const getNextAvailableLicense = (
  licenses: Array<LicenseTypeListItem>,
): LicenseTypeListItem | undefined =>
  [...licenses]
    .sort((a, b) => a.price - b.price)
    .find(({ availableAmount }) => availableAmount > 0);
